import type { RulesMulDivFlag } from './rulesMulDivProblems';
import type { RulesPanelCopy } from './rulesPanelCopy';

/**
 * Rules-panel copy for all sixteen `VIFRulesMulDiv` variants.
 *
 * PLACEHOLDER CONTENT - NOT SIGNED OFF. As with `rulesAddSubCopy.ts`'s
 * `RULES_ADD_SUB_COPY`, the real rules-panel text does not exist as text in
 * this repository: `rulesclip`'s `ruleHdr`/`ruleTxt` sub-symbols are
 * outlined vector artwork selected by frame, not strings. AddSub's copy was
 * recovered by mounting that composition headless and reading each frame -
 * that recovery step was not available for this port (a different FLA
 * composition, not inspected here). The `title`/`subtitle`/`body`/`example`
 * below are therefore derived only from the short rule-name comments already
 * present in `VIFRulesMulDiv.computeGameFlag`
 * (packages/modules/vif/src/games/VIFRulesMulDiv.ts:273-311) - e.g. "zero-one
 * rule", "mirror rule", "tens by ones" - worded in AddSub's copy style. They
 * read sensibly but are NOT verified against the shipped artwork or approved
 * by anyone. Treat this file as a structural placeholder: it makes every
 * variant render real, complete text so the board is usable and testable,
 * but the wording needs client/design sign-off before this ships to
 * players.
 *
 * One further wrinkle worth flagging explicitly: flags 11 and 14 are
 * commented "Multiply ..." in `computeGameFlag`, but `computeOperation`
 * reports both as division (`gameFlag > 9`). That operation value is ported
 * verbatim, not "corrected" - see the audit note in
 * docs/historical/plans/2026-08-vif-top-canvas-scope-audit-gaps.md (G4) - so
 * this file's `operation: 'divide'` for both is intentional even though the
 * body copy below describes a division reading of the comment's "multiply"
 * label. That reading also needs confirming.
 */
export const RULES_MUL_DIV_COPY: Record<RulesMulDivFlag, RulesPanelCopy> = {
  0: {
    title: 'VIFs Multiply',
    subtitle: 'ZERO and ONE Rule',
    body: 'Multiply any number by 0 and the product is 0. Multiply any number by 1 and it stays the same.',
    operation: 'multiply',
  },
  1: {
    title: 'VIFs Multiply',
    subtitle: 'TENS by TENS',
    body: 'Multiply two multiples of ten.',
    example: '10 × 30 = 300',
    operation: 'multiply',
  },
  2: {
    title: 'VIFs Multiply',
    subtitle: 'MIRROR Rule',
    body: 'Multiply the numbers in any order and the product is the same.',
    operation: 'multiply',
  },
  3: {
    title: 'VIFs Multiply',
    subtitle: 'Double Digits by Single Digits',
    body: 'Multiply a double-digit number by a single-digit number.',
    operation: 'multiply',
  },
  4: {
    title: 'VIFs Multiply',
    subtitle: 'Tens by Ones',
    body: 'Multiply a multiple of ten by a single-digit number.',
    operation: 'multiply',
  },
  5: {
    title: 'VIFs Multiply',
    subtitle: 'Double Digits',
    body: 'Multiply any two double-digit numbers.',
    operation: 'multiply',
  },
  6: {
    title: 'VIFs Multiply',
    subtitle: '36 Facts',
    body: 'Match each fact to its product.',
    operation: 'multiply',
  },
  7: {
    title: 'VIFs Multiply',
    subtitle: 'Auditory Memory (Part 1)',
    body: 'Match each fact from 2 × 2 to 4 × 9 to its product.',
    operation: 'multiply',
  },
  8: {
    title: 'VIFs Multiply',
    subtitle: 'Auditory Memory (Part 2)',
    body: 'Match each fact from 5 × 5 to 9 × 9 to its product.',
    operation: 'multiply',
  },
  10: {
    title: 'VIFs Divide',
    subtitle: 'ZERO and ONE Rule',
    body: 'Divide any number by 1 and it stays the same. Divide 0 by any number and the quotient is 0.',
    operation: 'divide',
  },
  11: {
    // Comment-labelled "Multiply Double Digits by Tens" in computeGameFlag,
    // but computeOperation reports DIV for every flag above 9 - see the file
    // header. Body copy below assumes the division reading; unconfirmed.
    title: 'VIFs Divide',
    subtitle: 'Double Digits by Tens',
    body: 'Divide a double-digit number by a multiple of ten.',
    operation: 'divide',
  },
  12: {
    title: 'VIFs Divide',
    subtitle: 'Loose Mirror Rule',
    body: 'A division fact and its mirror multiplication fact are related - use one to find the other.',
    operation: 'divide',
  },
  13: {
    title: 'VIFs Divide',
    subtitle: 'Triple Digits by Single Digits',
    body: 'Divide a triple-digit number by a single-digit number.',
    operation: 'divide',
  },
  14: {
    // Comment-labelled "Multiply Single Digits by Tens" - see the flag 11
    // note above; same caveat applies here.
    title: 'VIFs Divide',
    subtitle: 'Single Digits by Tens',
    body: 'Divide a multiple of ten by a single-digit number.',
    operation: 'divide',
  },
  15: {
    title: 'VIFs Divide',
    subtitle: 'Multi Digits by Double Digits',
    body: 'Divide a multi-digit number by a double-digit number.',
    operation: 'divide',
  },
  16: {
    title: 'VIFs Divide',
    subtitle: '36 Facts Game',
    body: 'Practice dividing using all thirty-six division facts.',
    operation: 'divide',
  },
};
